import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import { usePageLoading } from '../../contexts/PageLoadingContext';
import {
  Menu as MenuIcon,
  CompareArrows as MappingIcon,
  PlayArrow as PipelineIcon,
  Logout as LogoutIcon,
  Label as BrandIcon,
  Hub as RelationshipsIcon,
  Receipt as InvoicingIcon,
  AccountBalance as CommissionsIcon,
  ShowChart as PerformanceIcon,
  Code as CodeExtractionIcon,
  Store as RetailersIcon,
  Block as StockVetoesIcon,
  Handshake as PartnerRulesIcon,
  Straighten as SizeNormalizationIcon,
} from '@mui/icons-material';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase-config';

const drawerWidth = 240;

const navItems = [
  { text: 'Performance', icon: <PerformanceIcon />, path: '/performance' },
  { text: 'Retailers', icon: <RetailersIcon />, path: '/retailers' },
  { text: 'Stock Vetoes', icon: <StockVetoesIcon />, path: '/stock-vetoes' },
  { text: 'Partner Rules', icon: <PartnerRulesIcon />, path: '/partner-rules' },
  { text: 'Pipeline', icon: <PipelineIcon />, path: '/pipeline' },
  { text: 'Relationships', icon: <RelationshipsIcon />, path: '/relationships' },
  { text: 'Commissions', icon: <CommissionsIcon />, path: '/commissions' },
  { text: 'Invoicing', icon: <InvoicingIcon />, path: '/invoicing' },
  { text: 'Brand Mapping', icon: <BrandIcon />, path: '/brands' },
  { text: 'Code Extraction', icon: <CodeExtractionIcon />, path: '/code-extraction' },
  { text: 'Attribute Mapping', icon: <MappingIcon />, path: '/mappings' },
  { text: 'Size Normalization', icon: <SizeNormalizationIcon />, path: '/size-normalization' },
];

export default function DashboardLayout({ children, user }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isLoading } = usePageLoading();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap>
          Handoff Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={
                item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path)
              }
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {navItems.find((item) =>
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path)
            )?.text || 'Admin'}
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.email}
          </Typography>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      {isLoading && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 64,
            left: { sm: `${drawerWidth}px` },
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar - 1,
            height: 3,
          }}
        />
      )}

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
